/**
 * Programmatic product-row blobs.
 *
 * A single requestAnimationFrame loop draws every visible blob. Each shape is
 * generated from layered radial waves and converted to a smooth closed Bezier
 * path, so no player runtime or remote animation asset is required.
 */
(function starryProductBlobs() {
  if (window.StarryProductBlobs) {
    window.StarryProductBlobs.init(document);
    return;
  }

  const blobSelector = '[data-starry-product-blob]';
  const svgNamespace = 'http://www.w3.org/2000/svg';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Set();
  let animationFrameId = 0;

  /**
   * @param {number} value
   * @returns {number}
   */
  function round(value) {
    return Math.round(value * 100) / 100;
  }

  /**
   * Turn evenly spaced radial points into a smooth, closed cubic Bezier path.
   *
   * @param {Array<{ x: number, y: number }>} points
   * @returns {string}
   */
  function smoothClosedPath(points) {
    const count = points.length;
    let path = `M ${round(points[0].x)} ${round(points[0].y)}`;

    for (let index = 0; index < count; index += 1) {
      const previous = points[(index - 1 + count) % count];
      const current = points[index];
      const next = points[(index + 1) % count];
      const afterNext = points[(index + 2) % count];
      const firstControl = {
        x: current.x + (next.x - previous.x) / 6,
        y: current.y + (next.y - previous.y) / 6,
      };
      const secondControl = {
        x: next.x - (afterNext.x - current.x) / 6,
        y: next.y - (afterNext.y - current.y) / 6,
      };

      path += [
        ' C ',
        round(firstControl.x),
        ' ',
        round(firstControl.y),
        ', ',
        round(secondControl.x),
        ' ',
        round(secondControl.y),
        ', ',
        round(next.x),
        ' ',
        round(next.y),
      ].join('');
    }

    return `${path} Z`;
  }

  /**
   * @param {number} time
   * @param {number} seed
   * @param {number} speed
   * @returns {string}
   */
  function createBlobPath(time, seed, speed) {
    const center = 128;
    const pointCount = 16;
    const scaledTime = time * speed * 1.6;
    const points = [];

    for (let index = 0; index < pointCount; index += 1) {
      const angle = (Math.PI * 2 * index) / pointCount;
      const primaryWave = Math.sin(angle * 3 + scaledTime * 0.00042 + seed * 0.61) * 5.5;
      const secondaryWave = Math.sin(angle * 5 - scaledTime * 0.00029 + seed * 1.17) * 2.5;
      const tertiaryWave = Math.cos(angle * 2 + scaledTime * 0.00018 + seed * 0.37) * 1.5;
      const breathing = Math.sin(scaledTime * 0.00035 + seed) * 1.5;
      const radius = 99 + primaryWave + secondaryWave + tertiaryWave + breathing;

      points.push({
        x: center + Math.cos(angle) * radius,
        y: center + Math.sin(angle) * radius,
      });
    }

    return smoothClosedPath(points);
  }

  /**
   * @param {HTMLElement} element
   */
  function createController(element) {
    if (element.dataset.blobInitialized === 'true') return;

    const seed = Number.parseFloat(element.dataset.blobSeed || '1');
    const phaseOffset = Number.parseFloat(element.dataset.blobOffset || '0');
    const speed = Number.parseFloat(element.dataset.blobSpeed || '1');
    const svg = document.createElementNS(svgNamespace, 'svg');
    const path = document.createElementNS(svgNamespace, 'path');

    svg.setAttribute('viewBox', '0 0 256 256');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('starry-product-blob__svg');
    path.classList.add('starry-product-blob__path');
    svg.append(path);
    element.append(svg);

    const controller = {
      element,
      path,
      seed: Number.isFinite(seed) ? seed : 1,
      phaseOffset: Number.isFinite(phaseOffset) ? phaseOffset : 0,
      speed: Number.isFinite(speed) ? speed : 1,
      visible: false,
      draw(time) {
        path.setAttribute('d', createBlobPath(time + this.phaseOffset, this.seed, this.speed));
      },
    };

    controller.draw(controller.seed * 997);
    element.dataset.blobInitialized = 'true';
    element.classList.add('starry-product-blob--enhanced');
    controllers.add(controller);
    intersectionObserver.observe(element);
  }

  /**
   * @param {number} time
   */
  function animate(time) {
    animationFrameId = 0;
    let hasVisibleBlobs = false;

    for (const controller of controllers) {
      if (!controller.element.isConnected) {
        intersectionObserver.unobserve(controller.element);
        controllers.delete(controller);
        continue;
      }

      if (!controller.visible || reducedMotionQuery.matches) continue;
      controller.draw(time);
      hasVisibleBlobs = true;
    }

    if (hasVisibleBlobs) {
      animationFrameId = window.requestAnimationFrame(animate);
    }
  }

  function startAnimationLoop() {
    if (animationFrameId || reducedMotionQuery.matches) return;
    animationFrameId = window.requestAnimationFrame(animate);
  }

  function stopAnimationLoop() {
    if (!animationFrameId) return;
    window.cancelAnimationFrame(animationFrameId);
    animationFrameId = 0;
  }

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const controller = Array.from(controllers).find(({ element }) => element === entry.target);
        if (!controller) continue;

        controller.visible = entry.isIntersecting;
      }

      if (entries.some((entry) => entry.isIntersecting)) {
        startAnimationLoop();
      } else if (![...controllers].some(({ visible }) => visible)) {
        stopAnimationLoop();
      }
    },
    {
      rootMargin: '120px 0px',
      threshold: 0.01,
    }
  );

  function handleMotionPreference() {
    if (reducedMotionQuery.matches) {
      stopAnimationLoop();
      for (const controller of controllers) {
        controller.draw(controller.seed * 997);
      }
      return;
    }

    if ([...controllers].some(({ visible }) => visible)) {
      startAnimationLoop();
    }
  }

  /**
   * @param {Document | Element} root
   */
  function init(root) {
    if (root instanceof HTMLElement && root.matches(blobSelector)) {
      createController(root);
    }

    root.querySelectorAll(blobSelector).forEach((element) => {
      if (element instanceof HTMLElement) createController(element);
    });
  }

  reducedMotionQuery.addEventListener('change', handleMotionPreference);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) init(event.target);
  });

  window.StarryProductBlobs = { init };
  init(document);
})();
